import { pick, cloneDeep } from 'lodash';

import db, { fetch } from './db';

import {
  WarriorType,
  SkillType,
  ThingType,
  TableExperienceType,
  IslandType,
  CombatType,
  InitDataType,
  BuildingType,
  BuildingDataType,
  WarriorLogsType,
  WarriorBonusType,
  WarriorBonusesType,
  BonusToType,
  QuestToType,
  FriendsType,
  FriendEntryType,
  MentorBonusType,
  WarriorAdViewsType,
  ClanType,
  ClanInviteType,
  ClanInvitesType,
} from './types';

import { mapObjToArray } from '../utils/utils';
import { countHp, mapWarrior, beforeSaveWarrior } from '../utils/warriorUtils';
import {
  mapCombat,
  beforeSaveCombat,
  fetchCombatWarriors,
  isTimeout,
  isWin,
  mapCombatObj,
} from '../utils/combatUtils';
import { sendAdminMsg } from '../components/Chat/utils';
import { COMBAT_STATUS_FIGHT } from './constants';
import { mapKeyObjToObj } from '../utils/utils';

export async function getWarriorByLogin(login: string) {
  const warriorRef = await db()
    .child('warriors')
    .orderByChild('loginLowerCase')
    .equalTo(login.toLowerCase())
    .once('value');

  const result = warriorRef.val();

  return result ? mapKeyObjToObj(result) : null;
}

export async function getSkills(): Promise<SkillType[]> {
  const skills = await db().child('skills').once('value');

  return skills.val();
}

export async function getThings(): Promise<ThingType[]> {
  const things = await db().child('things').once('value');

  return mapObjToArray(things.val());
}

export async function getIslands(): Promise<IslandType[]> {
  const islands = await db().child('islands').once('value');
  const val = islands.val();
  if (!val) return [];
  return Array.isArray(val) ? val.filter(Boolean) : mapObjToArray(val);
}

export async function getBuildings(): Promise<BuildingType[]> {
  const buildings = await db().child('buildings').once('value');
  const val = buildings.val();
  if (!val) return [];
  return Array.isArray(val) ? val.filter(Boolean) : mapObjToArray(val);
}

export async function getBonusesCatalog(): Promise<BonusToType[]> {
  const snap = await db().child('bonuses').once('value');
  const val = snap.val();
  if (!val) return [];
  return mapObjToArray(val);
}

export async function getQuestsCatalog(): Promise<QuestToType[]> {
  const snap = await db().child('quests').once('value');
  const val = snap.val();
  if (!val) return [];
  return mapObjToArray(val);
}

export async function getFriends(warriorId: string): Promise<FriendsType> {
  const snap = await db().child('friends').child(warriorId).once('value');
  const val = snap.val();
  if (!val) return [];
  return Array.isArray(val) ? val : Object.values(val);
}

export async function appendFriend(warriorId: string, entry: FriendEntryType): Promise<void> {
  const current = await getFriends(warriorId);
  current.push(entry);
  await db().child('friends').child(warriorId).set(current);
}

export async function newClan(clan: ClanType): Promise<string> {
  const ref = await db().child('clans').push(clan);
  const id = ref.key as string;
  clan.id = id;
  await db().child('clans').child(id).update({ id });
  return id;
}

export async function getClan(id: string): Promise<ClanType | null> {
  const snap = await db().child('clans').child(id).once('value');
  return snap.val() || null;
}

export async function getClanByName(name: string): Promise<ClanType | null> {
  const snap = await db()
    .child('clans')
    .orderByChild('nameLower')
    .equalTo(name.toLowerCase())
    .once('value');
  const val = snap.val();
  if (!val) return null;
  const first = mapKeyObjToObj(val);
  return first as ClanType;
}

export async function saveClan(clan: Partial<ClanType> & { id: string }): Promise<void> {
  await db().child('clans').child(clan.id).update(clan);
}

export async function removeClan(id: string): Promise<void> {
  await db().child('clans').child(id).remove();
}

export function onClanChanged(id: string, cb: Function) {
  const ref = db().child(`clans/${id}`);
  ref.on('value', cb);
  return ref;
}

export async function getTopClans(limit: number): Promise<ClanType[]> {
  const snap = await db().child('clans').once('value');
  const val = snap.val();
  if (!val) return [];
  return mapObjToArray(val)
    .sort((a: any, b: any) => (b.warWins || 0) - (a.warWins || 0))
    .slice(0, limit);
}

export async function setClanMember(
  clanId: string,
  warriorId: string,
  member: { login: string; joinedAt: number },
): Promise<void> {
  await db().child('clans').child(clanId).child('members').child(warriorId).set(member);
}

export async function removeClanMember(clanId: string, warriorId: string): Promise<void> {
  await db().child('clans').child(clanId).child('members').child(warriorId).remove();
}

export async function setClanWar(clanId: string, currentWar: any): Promise<void> {
  // set(null) removes the node; set(object) replaces it wholesale (no merge surprises).
  await db()
    .child('clans')
    .child(clanId)
    .child('currentWar')
    .set(currentWar || null);
}

export async function addClanInvite(warriorId: string, invite: ClanInviteType): Promise<void> {
  await db().child('clanInvites').child(warriorId).child(invite.clanId).set(invite);
}

export async function removeClanInvite(warriorId: string, clanId: string): Promise<void> {
  await db().child('clanInvites').child(warriorId).child(clanId).remove();
}

export async function getClanInvites(warriorId: string): Promise<ClanInvitesType> {
  const snap = await db().child('clanInvites').child(warriorId).once('value');
  const val = snap.val();
  if (!val) return [];
  return Object.values(val) as ClanInvitesType;
}

export function onClanInvitesChanged(warriorId: string, cb: Function) {
  const ref = db().child(`clanInvites/${warriorId}`);
  ref.on('value', cb);
  return ref;
}

export async function getMentorBonuses(): Promise<MentorBonusType[]> {
  const snap = await db().child('mentorBonuses').once('value');
  const val = snap.val();
  if (!val) return [];
  return mapObjToArray(val);
}

export async function getWarriorAdViews(warriorId: string): Promise<WarriorAdViewsType | null> {
  const snap = await db().child('warriorAdViews').child(warriorId).once('value');
  return snap.val() || null;
}

export async function saveWarriorAdViews(
  warriorId: string,
  value: WarriorAdViewsType,
): Promise<void> {
  await db().child('warriorAdViews').child(warriorId).set(value);
}

export async function getTableExperience(): Promise<TableExperienceType[]> {
  const tableExperience = await db().child('tableExperience').once('value');

  return tableExperience.val();
}

export async function getWarrior(id: string, initData?: InitDataType): Promise<WarriorType | null> {
  const warrior = await fetch(async () => {
    const result = await db().child('warriors').child(id).once('value');

    return result;
  });

  return warrior ? mapWarrior(warrior, initData) : null;
}

export async function saveWarrior(warrior: Partial<WarriorType>, notCleanId?: boolean) {
  const { id } = warrior;
  warrior = cloneDeep(warrior);
  beforeSaveWarrior(warrior as WarriorType, notCleanId);

  await db().child('warriors').child(id).update(warrior);
}

export async function getBotsOnIsland(
  island: string,
  x: number,
  y: number,
): Promise<WarriorType[]> {
  const DELTA = 3;

  const startX = Math.max(0, x - DELTA);
  const endX = x + DELTA;
  const startY = Math.max(0, y - DELTA);
  const endY = y + DELTA;

  const warriors = await fetch(async () => {
    const snap = await db()
      .child('warriors')
      .orderByChild('location/coordinateX')
      .startAt(startX)
      .endAt(endX)
      .once('value');
    return snap.val();
  });

  const now = Date.now();
  return !warriors
    ? []
    : mapObjToArray(warriors)
        .filter(
          (item: any) =>
            item.isBot &&
            item.location.island === island &&
            item.location.coordinateY >= startY &&
            item.location.coordinateY <= endY &&
            !(item.isMonster && (item.availableAt || 0) > now),
        )
        .map((item: any) => mapWarrior(item));
}

export async function saveCombat(combat: CombatType) {
  await beforeSaveCombat(combat);

  await db().child('combats').child(combat.id).update(combat);
}

export async function newCombat(combat: CombatType, warrior: WarriorType, initData: InitDataType) {
  const combatRef = await db().child('combats').push(combat);

  const id = combatRef.key;

  await fetchCombatWarriors(combat, warrior, initData, (combatWarrior: any) => {
    combatWarrior._warrior.combat = id;
    combatWarrior._warrior.feature.hp = countHp(combatWarrior._warrior.feature.hp);
  });

  combat.id = id;

  await saveCombat(combat);

  const promises = combat.warriors.map(
    (combatWarrior) =>
      new Promise((resolve) => {
        saveWarrior(pick(combatWarrior._warrior, ['id', 'combat', 'feature', 'isBot'])).then(
          resolve,
        );
      }),
  );

  await Promise.all(promises);

  await sendAdminMsg(
    {
      message: `Combat ${combat.id} ${warrior.login} ${warrior.id} has started`,
    } as any,
    true,
  );
}

export async function getCombat(
  id: string,
  warrior?: WarriorType,
  initData?: InitDataType,
  noWarriorsFetch?: boolean,
): Promise<CombatType> {
  const value = await fetch(async () => {
    let combat = await db().child('combats').child(id).once('value');

    combat = combat.val();
    combat.id = id;

    await mapCombat(combat, warrior, initData, noWarriorsFetch);
    return combat;
  });

  return value;
}

export async function getBotsInCombat() {
  let botsInCombat = await db()
    .child('warriors')
    .orderByChild('combat')
    .startAt('!')
    .endAt('~')
    .once('value');

  botsInCombat = botsInCombat.val();

  return !botsInCombat ? [] : mapObjToArray(botsInCombat).filter((item: any) => item.isBot);
}

export async function getConfig() {
  const snapshot = await db().child('config').once('value');

  return snapshot.val();
}

export const initDb = () => {
  db();
};

export function onWarriorChanged(id: string, cb: Function) {
  const ref = db().child(`warriors/${id}`);
  ref.on('value', cb);
  return ref;
}

export function onWarriorBonusesChanged(id: string, cb: Function) {
  const ref = db().child(`warriorBonuses/${id}`);
  ref.on('value', cb);
  return ref;
}

export function onFriendsChanged(id: string, cb: Function) {
  const ref = db().child(`friends/${id}`);
  ref.on('value', cb);
  return ref;
}

export function onWarriorAdViewsChanged(id: string, cb: Function) {
  const ref = db().child(`warriorAdViews/${id}`);
  ref.on('value', cb);
  return ref;
}

export function onCombatChanged(id: string, cb: Function) {
  const ref = db().child(`combats/${id}`);
  ref.on('value', cb);

  return ref;
}

export async function getBuildingData(buildingId: string): Promise<BuildingDataType> {
  const value = await fetch(async () => {
    const result = await db()
      .child('buildingsData')
      .orderByChild('building')
      .equalTo(buildingId)
      .once('value');
    return result;
  });

  return value[Object.keys(value)[0]];
}

export function onBuildingDataChanged(id: string, cb: Function) {
  const ref = db().child(`buildingsData/${id}`);
  ref.on('value', cb);

  return ref;
}

export async function saveBuildingData(buildingData: BuildingDataType) {
  await db().child('buildingsData').child(buildingData.id).update(buildingData);
}

export function onUserStatusChanged(cb: Function) {
  const ref = db().child('.info/connected');
  ref.on('value', (snapshot: any) => {
    cb(snapshot.val());
  });

  return ref;
}

export function onWarriorDisconnect(id: string, value: { status: 'online' | 'offline' }) {
  db().child(`warriors/${id}`).onDisconnect().update(value);
}

export async function getOnlineWarriors(
  initData: InitDataType,
  island?: string,
): Promise<WarriorType[]> {
  const warriors = await fetch(async () => {
    const result = await db()
      .child('warriors')
      .orderByChild('status')
      .equalTo('online')
      .once('value');

    return result;
  });

  if (!warriors) return [];
  let list = mapObjToArray(warriors).map((item: any) => mapWarrior(item, initData));
  if (island) {
    list = list.filter((item: any) => item.location.island === island);
  }
  return list;
}

export async function getCombats() {
  const combats = await fetch(async () => {
    const result = await db()
      .child('combats')
      .orderByChild('status')
      .equalTo(COMBAT_STATUS_FIGHT)
      .once('value');

    return result;
  });

  const filter = (item: any) => !isTimeout(item);

  return !combats ? [] : mapObjToArray(combats).map(mapCombatObj).filter(filter);
}

export async function getPrivateData(path: string) {
  const data = await db().child(`_private/${path}`).once('value');

  return data.val();
}

export async function savePrivateData(path: string, data: {}) {
  await db().child(`_private/${path}`).update(data);
}

export async function removePrivateData(path: string) {
  await db().child(`_private/${path}`).remove();
}

export async function deleteUserWarrior(firebaseAdmin: any, id: string) {
  await firebaseAdmin.auth().deleteUser(id);
  await db().child('warriors').child(id).remove();
}

export async function getTransaction({
  warriorId,
  objectId,
  amount,
  transactionId,
}: {
  warriorId: string;
  objectId: string;
  amount: number;
  transactionId?: string;
}) {
  const transactions = await db().child('transactions').once('value');

  const values = mapObjToArray(transactions.val() || {});
  const value = values.find((item: any) => {
    const selectedItem =
      item.warriorId === warriorId &&
      item.objectId === objectId &&
      item.amount === amount &&
      !item.payed &&
      !item.processing;
    if (transactionId && selectedItem) return item.id === transactionId;
    return selectedItem;
  });

  if (value || transactionId) return value;

  const transactionRef = await db().child('transactions').push({
    warriorId,
    objectId,
    amount,
    payed: false,
  });

  const id = transactionRef.key;
  return {
    id,
    warriorId,
    objectId,
    amount,
    payed: false,
  };
}

export async function saveTransaction(id: string, data: any) {
  await db().child('transactions').child(id).update(data);
}

export async function getApplePurchase(transactionId: string) {
  const snapshot = await db().child('applePurchases').child(transactionId).once('value');
  return snapshot.val();
}

export async function saveApplePurchase(transactionId: string, data: any) {
  await db().child('applePurchases').child(transactionId).set(data);
}

export async function addLoginLog(warriorId: string) {
  const ref = db().child(`logs/${warriorId}/logins`);
  const snapshot = await ref.once('value');
  const existing = snapshot.val();
  const arr = Array.isArray(existing) ? existing : [];
  arr.push({ timestamp: Date.now() });
  await ref.set(arr);
}

export async function addCombatLog(warrior: WarriorType, combat: CombatType) {
  const ref = db().child(`logs/${warrior.id}/combats`);
  const snapshot = await ref.once('value');
  const existing = snapshot.val();
  const arr = Array.isArray(existing) ? existing : [];
  const w = isWin(combat, warrior);
  arr.push({
    _combat: { id: combat.id, finished: combat.finished },
    result: w === 1 ? 'win' : w === -1 ? 'draft' : 'loose',
  });
  await ref.set(arr);
}

export async function getWarriorLogs(warriorId: string): Promise<WarriorLogsType> {
  const snap = await db().child(`logs/${warriorId}`).once('value');
  return snap.val() || {};
}

export async function getWarriorBonuses(warriorId: string): Promise<WarriorBonusesType> {
  const snap = await db().child(`warriorBonuses/${warriorId}`).once('value');
  const val = snap.val();
  return Array.isArray(val) ? val : [];
}

export async function addWarriorBonus(warriorId: string, bonus: WarriorBonusType) {
  const ref = db().child(`warriorBonuses/${warriorId}`);
  const snap = await ref.once('value');
  const existing = snap.val();
  const arr = Array.isArray(existing) ? existing : [];
  arr.push(bonus);
  await ref.set(arr);
}

export async function saveWarriorBonuses(warriorId: string, bonuses: WarriorBonusesType) {
  await db().child(`warriorBonuses/${warriorId}`).set(bonuses);
}

// --- Arena (ranked async PvP) ---

/** Lightweight non-bot warrior pool for matchmaking (only the fields pickOpponent needs). */
export async function getArenaPool(): Promise<
  { id: string; level: number; combat?: string; arena?: any }[]
> {
  const val = await fetch(async () => (await db().child('warriors').once('value')).val());
  return !val
    ? []
    : mapObjToArray(val)
        .filter((w: any) => !w.isBot)
        .map((w: any) => ({ id: w.id, level: w.level, combat: w.combat, arena: w.arena }));
}

/** Persist just the arena sub-record on a warrior (rating/tickets/season W-L). */
export async function saveWarriorArena(warriorId: string, arena: any): Promise<void> {
  await db().child('warriors').child(warriorId).child('arena').update(arena);
}

/** Ranked read model for the leaderboard, keyed by season then warrior. */
export async function setArenaStanding(
  seasonId: string,
  warriorId: string,
  standing: { login: string; rating: number; wins: number; losses: number },
): Promise<void> {
  await db().child('arenaStandings').child(seasonId).child(warriorId).set(standing);
}

export async function getArenaStandings(
  seasonId: string,
): Promise<{ id: string; login: string; rating: number; wins: number; losses: number }[]> {
  const val = await fetch(async () =>
    (await db().child('arenaStandings').child(seasonId).once('value')).val(),
  );
  return val ? (mapObjToArray(val) as any) : [];
}

/** Append a "who fought me" entry to a warrior's arena match history. */
export async function addArenaMatchLog(
  warriorId: string,
  entry: { opponentLogin: string; result: 'win' | 'loss'; delta: number; at: number },
): Promise<void> {
  await db().child('arenaMatchLogs').child(warriorId).push(entry);
}

/** Create a throwaway arena bot warrior under a fresh push id; returns the id. */
export async function createArenaBot(warrior: WarriorType): Promise<string> {
  const id = db().child('warriors').push().key as string;
  warrior.id = id;
  warrior.isBot = true;
  await saveWarrior(warrior, true);
  // saveWarrior/beforeSaveWarrior strips isBot before the update(); harmless for
  // existing bots (merge leaves the stored flag), but a freshly created record
  // would land without it — so the clone would fight as a real (silent) player.
  // Write the flag explicitly so attack() auto-generates the bot's strikes.
  await db().child('warriors').child(id).child('isBot').set(true);
  return id;
}

/** Remove a materialized arena bot clone once its fight is over. */
export async function removeWarrior(id: string): Promise<void> {
  await db().child('warriors').child(id).remove();
}
